import {
    getDefaultOpeningBalanceAdjustmentDate,
    getFinancialAccountOpeningBalanceDate,
    getFinancialAccountOpeningBalanceDescription,
    shouldDebitAccountWithOpeningBalance,
} from "@common/helpers/openingBalance";
import { Account } from "@common/types/domains/accounting/account";
import { Entity } from "@common/types/entity";
import { useMemo } from "react";
import { useFormikContext } from "formik";
import classNames from "classnames";
import { FinancialAccount } from "@common/types/financialAccount";
import { MainOpeningBalance } from "@common/schemas/domains/accounting/opening-balance.schema";
import styles from "./FinancialAccountOpeningBalance.module.scss";
import { OpeningBalanceForm } from "./helpers";
import {
    JournalEntryPreview,
    JournalEntryPreviewSheetRow,
} from "./JournalEntryPreview";

interface Props {
    accountInLedger?: Account;
    entity: Entity;
    financialAccount: FinancialAccount;
    accounts: Account[];
    shouldCreateJournalEntry: boolean;
    isOpeningBalanceLocked: boolean;
    existingOpeningBalance?: MainOpeningBalance | null;
}

export const OpeningBalanceJournalEntryPreview: React.FC<Props> = ({
    accountInLedger,
    entity,
    financialAccount,
    accounts,
    shouldCreateJournalEntry,
    isOpeningBalanceLocked,
    existingOpeningBalance,
}) => {
    const { values } = useFormikContext<OpeningBalanceForm>();
    const {
        openingBalance,
        clearingAccountId,
        adjustment,
        adjustmentClearingAccountId,
    } = values;

    const obDate =
        existingOpeningBalance?.date ??
        getFinancialAccountOpeningBalanceDate(entity, financialAccount);

    const adjustmentDate = useMemo(
        () =>
            getDefaultOpeningBalanceAdjustmentDate(
                getFinancialAccountOpeningBalanceDate(entity, financialAccount),
            ),
        [entity, financialAccount],
    );

    const shouldDebitAccount = useMemo(
        () =>
            accountInLedger
                ? shouldDebitAccountWithOpeningBalance(
                      accountInLedger!,
                      openingBalance ?? 0,
                  )
                : false,
        [accountInLedger, openingBalance],
    );

    const hasOpeningBalance =
        typeof openingBalance === "number" && openingBalance !== 0;
    const hasAdjustment = typeof adjustment === "number" && adjustment !== 0;

    const obRows: JournalEntryPreviewSheetRow[] = useMemo(() => {
        if (!shouldCreateJournalEntry || !hasOpeningBalance) {
            return [{}, {}];
        }

        const description = isOpeningBalanceLocked
            ? ""
            : getFinancialAccountOpeningBalanceDescription(
                  entity,
                  financialAccount,
              );

        return [
            {
                accountId: accountInLedger?.id,
                [shouldDebitAccount ? "debitAmount" : "creditAmount"]: Math.abs(
                    openingBalance!,
                ),
                description,
            },
            {
                accountId: clearingAccountId,
                [shouldDebitAccount ? "creditAmount" : "debitAmount"]: Math.abs(
                    openingBalance!,
                ),
                description,
            },
        ];
    }, [
        accountInLedger,
        openingBalance,
        shouldDebitAccount,
        clearingAccountId,
        entity,
        financialAccount,
        shouldCreateJournalEntry,
        hasOpeningBalance,
        isOpeningBalanceLocked,
    ]);

    const adjustmentRows: JournalEntryPreviewSheetRow[] = useMemo(() => {
        if (!hasAdjustment) {
            return [];
        }

        const shouldDebitAdjustment = accountInLedger
            ? shouldDebitAccountWithOpeningBalance(accountInLedger, adjustment!)
            : false;
        const adjustmentCounterAccountId =
            adjustmentClearingAccountId ?? clearingAccountId;

        return [
            {
                accountId: accountInLedger?.id,
                [shouldDebitAdjustment ? "debitAmount" : "creditAmount"]:
                    Math.abs(adjustment!),
                description: "Opening balance adjustment",
            },
            {
                accountId: adjustmentCounterAccountId,
                [shouldDebitAdjustment ? "creditAmount" : "debitAmount"]:
                    Math.abs(adjustment!),
                description: "Opening balance adjustment",
            },
        ];
    }, [
        accountInLedger,
        adjustment,
        adjustmentClearingAccountId,
        clearingAccountId,
        hasAdjustment,
    ]);

    const showMainOpeningBalanceJe = !!openingBalance || !hasAdjustment;

    return (
        <>
            {showMainOpeningBalanceJe && (
                <JournalEntryPreview
                    lines={obRows}
                    title={
                        hasAdjustment ||
                        (isOpeningBalanceLocked && !!openingBalance)
                            ? "Opening balance journal entry"
                            : "New journal entry"
                    }
                    date={obDate}
                    entity={entity}
                    accounts={accounts}
                    className={classNames({
                        [styles.disabledJournalEntryPreview]:
                            !shouldCreateJournalEntry,
                    })}
                />
            )}
            {hasAdjustment && (
                <JournalEntryPreview
                    lines={adjustmentRows}
                    title="Adjustment journal entry"
                    date={adjustmentDate}
                    entity={entity}
                    accounts={accounts}
                />
            )}
        </>
    );
};
